/**
 * Context Service
 * Builds infrastructure context for RAG (Retrieval Augmented Generation)
 *
 * @module ContextService
 * Sprint 32: OOA/OOD Refactoring
 */

/**
 * Interface for context service
 */
export interface IContextService {
    getInfrastructureContext(): Promise<string>;
}

/**
 * Service for building infrastructure context for AI queries
 */
export class ContextService implements IContextService {
    private canisterClient: any = null;

    constructor() {
        if (typeof window !== 'undefined') {
            this.canisterClient = (window as any).canisterClient;
        }
    }

    /**
     * Convert vector_db metadata from Array<[string, string]> tuples to a plain object.
     * vector_db returns metadata as: [["hostname", "web-nginx-01"], ["ip", "192.168.1.25"], ...]
     */
    private metadataToObject(metadata: any): Record<string, string> {
        if (Array.isArray(metadata)) {
            const obj: Record<string, string> = {};
            for (const entry of metadata) {
                if (Array.isArray(entry) && entry.length >= 2) {
                    obj[entry[0]] = entry[1];
                }
            }
            return obj;
        }
        if (typeof metadata === 'object' && metadata !== null) {
            return metadata;
        }
        return {};
    }

    /**
     * Get infrastructure context from vector_db for RAG
     */
    async getInfrastructureContext(): Promise<string> {
        try {
            if (!this.canisterClient) {
                return 'No infrastructure data available - canister client not initialized.';
            }

            // Fetch devices, applications, relationships, and service maps in parallel
            const [devicesResponse, applicationsResponse, relationshipsResponse, serviceMapNamesResponse] = await Promise.all([
                this.canisterClient.getAllDevices(),
                this.canisterClient.getAllApplications(),
                this.canisterClient.getAllRelationships(),
                this.canisterClient.callCanister('vector_db', 'list_service_map_names', [])
            ]);

            console.log('Raw devices response:', devicesResponse);
            console.log('Raw applications response:', applicationsResponse);
            console.log('Raw relationships response:', relationshipsResponse);
            console.log('Raw service map names response:', serviceMapNamesResponse);

            // Handle response formats
            const devices = devicesResponse?.ok || devicesResponse?.data || devicesResponse || [];
            const applications = applicationsResponse?.ok || applicationsResponse?.data || applicationsResponse || [];
            const relationships = relationshipsResponse?.ok || relationshipsResponse?.data || relationshipsResponse || [];
            const serviceMapNames = serviceMapNamesResponse?.data || serviceMapNamesResponse || [];

            // Build context
            let context = `=== INFRASTRUCTURE SUMMARY ===\n`;
            context += `Total Devices: ${Array.isArray(devices) ? devices.length : 0}\n`;
            context += `Total Applications/Services: ${Array.isArray(applications) ? applications.length : 0}\n`;
            context += `Total Relationships: ${Array.isArray(relationships) ? relationships.length : 0}\n`;
            context += `Total Service Maps: ${Array.isArray(serviceMapNames) ? serviceMapNames.length : 0}\n`;

            // === DEVICES SECTION ===
            context += this.buildDevicesSection(devices);

            // === APPLICATIONS/SERVICES SECTION ===
            context += this.buildApplicationsSection(applications);

            // === RELATIONSHIPS SECTION ===
            context += this.buildRelationshipsSection(relationships);

            // === SERVICE MAPS SECTION ===
            context += await this.buildServiceMapsSection(serviceMapNames);

            console.log('Infrastructure context built:', context.substring(0, 800) + '...');
            return context;

        } catch (error) {
            console.error('Error fetching infrastructure context:', error);
            return 'Error fetching infrastructure data. Please try again.';
        }
    }

    /**
     * Build devices section of context
     */
    private buildDevicesSection(devices: any[]): string {
        if (!Array.isArray(devices) || devices.length === 0) {
            return `\n=== DEVICES ===\nNo devices found.\n`;
        }

        console.log(`Found ${devices.length} devices`);
        let section = `\n=== DEVICES ===\n`;

        devices.forEach((device: any, index: number) => {
            // vector_db returns [deviceId, Array<[string, string]>] tuples
            const deviceId = Array.isArray(device) ? device[0] : '';
            const rawMetadata = Array.isArray(device) ? device[1] : device;
            const deviceData = this.metadataToObject(rawMetadata);
            section += `\n[Device ${index + 1}] ${deviceId}\n`;

            if (deviceData.hostname) section += `  Hostname: ${deviceData.hostname}\n`;
            if (deviceData.ip) section += `  IP: ${deviceData.ip}\n`;
            if (deviceData.os) section += `  OS: ${deviceData.os}\n`;
            if (deviceData.device_type) section += `  Type: ${deviceData.device_type}\n`;
            if (deviceData.status) section += `  Status: ${deviceData.status}\n`;
        });

        return section;
    }

    /**
     * Build applications section of context
     */
    private buildApplicationsSection(applications: any[]): string {
        if (!Array.isArray(applications) || applications.length === 0) {
            return `\n=== APPLICATIONS & SERVICES ===\nNo applications found.\n`;
        }

        console.log(`Found ${applications.length} applications`);
        let section = `\n=== APPLICATIONS & SERVICES ===\n`;

        applications.forEach((app: any, index: number) => {
            // vector_db returns [appId, Array<[string, string]>] tuples
            const appId = Array.isArray(app) ? app[0] : '';
            const rawMetadata = Array.isArray(app) ? app[1] : app;
            const appData = this.metadataToObject(rawMetadata);
            section += `\n[Application ${index + 1}] ${appId}\n`;

            if (appData.name) section += `  Name: ${appData.name}\n`;
            if (appData.application_id) section += `  ID: ${appData.application_id}\n`;
            if (appData.app_type) section += `  Type: ${appData.app_type}\n`;
            if (appData.version) section += `  Version: ${appData.version}\n`;
            if (appData.vendor) section += `  Vendor: ${appData.vendor}\n`;
            if (appData.status) section += `  Status: ${appData.status}\n`;
            if (appData.port) section += `  Port: ${appData.port}\n`;
            if (appData.protocol) section += `  Protocol: ${appData.protocol}\n`;
        });

        return section;
    }

    /**
     * Build relationships section of context
     */
    private buildRelationshipsSection(relationships: any[]): string {
        if (!Array.isArray(relationships) || relationships.length === 0) {
            return `\n=== DEVICE-APPLICATION RELATIONSHIPS ===\nNo relationships found.\n`;
        }

        console.log(`Found ${relationships.length} relationships`);
        let section = `\n=== DEVICE-APPLICATION RELATIONSHIPS ===\n`;

        relationships.forEach((rel: any, index: number) => {
            // vector_db returns [relId, Array<[string, string]>] tuples
            const relId = Array.isArray(rel) ? rel[0] : '';
            const rawMetadata = Array.isArray(rel) ? rel[1] : rel;
            const relData = this.metadataToObject(rawMetadata);
            section += `\n[Relationship ${index + 1}] ${relId}\n`;

            if (relData.device_id) section += `  Device: ${relData.device_id}\n`;
            if (relData.application_id) section += `  Application: ${relData.application_id}\n`;
            if (relData.relationship_type) section += `  Type: ${relData.relationship_type}\n`;
        });

        return section;
    }

    /**
     * Build service maps section of context
     */
    private async buildServiceMapsSection(serviceMapNames: string[]): Promise<string> {
        if (!Array.isArray(serviceMapNames) || serviceMapNames.length === 0) {
            return `\n=== SERVICE MAPS ===\nNo service maps found.\n`;
        }

        console.log(`Found ${serviceMapNames.length} service maps:`, serviceMapNames);
        let section = `\n=== SERVICE MAPS ===\n`;
        section += `Available service maps: ${serviceMapNames.join(', ')}\n`;

        // Fetch detailed context for each service map
        for (const serviceName of serviceMapNames) {
            try {
                const serviceContextResponse = await this.canisterClient.callCanister(
                    'vector_db',
                    'get_service_context',
                    [serviceName]
                );
                const serviceContext = serviceContextResponse?.data || serviceContextResponse || '';
                if (serviceContext) {
                    section += `\n${serviceContext}\n`;
                }
            } catch (err) {
                console.warn(`Failed to fetch context for service map "${serviceName}":`, err);
            }
        }

        return section;
    }
}
